import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Trash2, Calendar, User, Mail, Phone, MessageSquare, Inbox, Database, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { UserLayout } from '../components/User/UserLayout';

interface Submission {
  id: string;
  submission_data: Record<string, string>;
  user_agent: string;
  ip_address: string;
  created_at: string;
}

interface PageInfo {
  id: string;
  name: string;
}

export const FormSubmissions: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const { token } = useAuthStore();
  
  const [page, setPage] = useState<PageInfo | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

  useEffect(() => {
    loadSubmissions();
  }, [pageId]);

  const loadSubmissions = async () => {
    try {
      setIsLoading(true);
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      
      const response = await fetch(`${API_URL}/submissions/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load submissions');
      }

      const data = await response.json();
      setPage(data.page);
      setSubmissions(data.submissions);
    } catch (error) {
      console.error('Load submissions error:', error);
      toast.error('Failed to load form submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (submissionId: string) => {
    if (!confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      
      const response = await fetch(`${API_URL}/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete submission');
      }

      toast.success('Submission deleted');
      loadSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Delete submission error:', error);
      toast.error('Failed to delete submission');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getFieldIcon = (fieldName: string) => {
    const lower = fieldName.toLowerCase();
    if (lower.includes('email') || lower.includes('e-mail')) return <Mail size={16} />;
    if (lower.includes('phone') || lower.includes('tel') || lower.includes('mobile')) return <Phone size={16} />;
    if (lower.includes('name') || lower.includes('ชื่อ')) return <User size={16} />;
    if (lower.includes('message') || lower.includes('comment') || lower.includes('ข้อความ')) return <MessageSquare size={16} />;
    return <FileText size={16} />;
  };

  const latestSubmission = submissions[0];
  const last24h = submissions.filter(s => {
    const diff = Date.now() - new Date(s.created_at).getTime();
    return diff < 24 * 60 * 60 * 1000;
  }).length;

  const handleExport = () => {
    if (submissions.length === 0) {
      toast.error('No submissions to export');
      return;
    }

    // Convert to CSV
    const headers = ['Date', 'Time', ...Object.keys(submissions[0].submission_data), 'IP Address', 'User Agent'];
    const rows = submissions.map(s => {
      const date = new Date(s.created_at);
      return [
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        ...Object.values(s.submission_data),
        s.ip_address || 'N/A',
        s.user_agent || 'N/A'
      ].map(v => `"${v}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-submissions-${page?.name}-${new Date().toISOString()}.csv`;
    a.click();
    toast.success('Exported to CSV!');
  };

  if (isLoading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading submissions...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">📋 Form Submissions</h1>
            <p className="text-gray-600 mt-1">
              Page: <span className="font-semibold">{page?.name}</span>
            </p>
          </div>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
            disabled={submissions.length === 0}
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Submissions</p>
              <Database className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{submissions.length}</p>
            <p className="text-xs text-gray-400 mt-2">All time</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Last 24 Hours</p>
              <Inbox className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{last24h}</p>
            <p className="text-xs text-gray-400 mt-2">Recent submissions</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Latest Submission</p>
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-lg font-bold text-gray-900">
              {latestSubmission ? formatDate(latestSubmission.created_at).split(',')[0] : 'N/A'}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {latestSubmission ? formatDate(latestSubmission.created_at).split(',')[1] : ''}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Form Fields</p>
              <FileText className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {latestSubmission ? Object.keys(latestSubmission.submission_data).length : 0}
            </p>
            <p className="text-xs text-gray-400 mt-2">Fields per submission</p>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Submissions List */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">All Submissions</h2>
            
            {submissions.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Inbox size={64} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No submissions yet</h3>
                <p className="text-gray-600">Submissions will appear here when users fill out your form.</p>
              </div>
            ) : (
              submissions.map((submission) => (
                <div
                  key={submission.id}
                  onClick={() => setSelectedSubmission(submission)}
                  className={`bg-white border ${
                    selectedSubmission?.id === submission.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                  } rounded-xl p-4 cursor-pointer transition-all hover:shadow-md hover:border-blue-300`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar size={14} />
                      {formatDate(submission.created_at)}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(submission.id);
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {Object.entries(submission.submission_data).slice(0, 2).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2">
                        <span className="text-gray-400 mt-0.5">{getFieldIcon(key)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-500">{key}</div>
                          <div className="text-sm text-gray-900 truncate">{value}</div>
                        </div>
                      </div>
                    ))}
                    {Object.keys(submission.submission_data).length > 2 && (
                      <div className="text-xs text-blue-600 font-medium">
                        +{Object.keys(submission.submission_data).length - 2} more fields
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Submission Detail */}
          <div className="lg:sticky lg:top-8 h-fit">
            {selectedSubmission ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Submission Details</h2>
                  <button
                    onClick={() => handleDelete(selectedSubmission.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>

                {/* Submission Data */}
                <div className="space-y-4 mb-6">
                  {Object.entries(selectedSubmission.submission_data).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-gray-400">{getFieldIcon(key)}</span>
                        <label className="text-sm font-semibold text-gray-700">{key}</label>
                      </div>
                      <div className="text-gray-900 whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg">
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Metadata */}
                <div className="border-t border-gray-200 pt-6 space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar size={16} className="text-gray-400" />
                    <span className="text-gray-600">Submitted:</span>
                    <span className="font-semibold text-gray-900">{formatDate(selectedSubmission.created_at)}</span>
                  </div>
                  
                  {selectedSubmission.ip_address && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-gray-600">IP:</span>
                      <span className="font-mono text-gray-900">{selectedSubmission.ip_address}</span>
                    </div>
                  )}
                  
                  {selectedSubmission.user_agent && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-gray-600 flex-shrink-0">Device:</span>
                      <span className="text-gray-900 break-all">{selectedSubmission.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <FileText size={64} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Select a submission</h3>
                <p className="text-gray-600">Click on a submission to view its details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </UserLayout>
  );
};

